// Which language a receipt prints its translations in.
//
// Pure, and deliberately derived from the tenant's OWN language set rather
// than read back out of localStorage. That distinction is the whole point:
// `receiptTranslationLanguage` is a value LanguageContext writes, and the
// Receipt Translations toggle used to read it directly —
//
//     const storedLang = localStorage.getItem('receiptTranslationLanguage');
//     if (storedLang && storedLang !== receiptTranslationLanguage) { ... }
//
// — so flipping the toggle before LanguageContext had written that key
// copied nothing, and translations went "on" with no language at all. A
// silent no-op with nothing at the till to show for it (Notion item 14).
// Deriving from `config.languages` removes that state rather than adding UI
// to explain it: any tenant with a second language always resolves to one,
// and a tenant with only English cannot enable the toggle in the first
// place.
//
// Shared by LanguageContext (which owns the stored value) and the POS
// toggle (which reads it), so the two cannot disagree about what the
// language is.

const asLanguageList = (languages) => (Array.isArray(languages) ? languages : []);

/**
 * Whether receipt translations are meaningful for this tenant at all.
 *
 * A single-language tenant has nothing to translate INTO, so the toggle is
 * hidden rather than shown-and-disabled — a dead switch invites "why can't I
 * turn this on?", and there is no answer that helps the cashier.
 *
 * @param {string[]|undefined|null} languages - tenant config `languages`
 * @returns {boolean}
 */
export function canTranslateReceipts(languages) {
    return asLanguageList(languages).length > 1;
}

/**
 * The language receipts should print translations in, or null when the
 * tenant cannot translate at all.
 *
 * A stored preference wins only if the tenant still HAS that language — a
 * tenant can drop a language from their config long after a till stored it,
 * and printing into a language that is no longer configured would render
 * nothing.
 *
 * @param {string[]|undefined|null} languages - tenant config `languages`
 * @param {string|null|undefined} storedLanguage - previously chosen value
 * @returns {string|null}
 */
export function resolveReceiptLanguage(languages, storedLanguage) {
    const list = asLanguageList(languages);
    if (list.length <= 1) return null;
    if (storedLanguage && list.includes(storedLanguage)) return storedLanguage;
    // First non-English, because English is the base the receipt is already
    // printed in. `list[0]` is the fallback for a tenant with no English at
    // all — indexing to list[1] blindly would pick their SECOND language.
    return list.find((lang) => lang !== 'en') || list[0];
}
